import { App } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { RecordTemplateDecisionDto } from '@pams/shared';
import { api, type ApiError } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import { DECISIONS_COPY } from '../copy';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [P7C §4] SAVING ONE DECISION — the decisions page's own save, moved here verbatim so the library's
 *  preview saves the same way ([C3a]: an action two screens perform is one flow, shared).
 *
 *  ⚠️ THE ORDER IS THE PAGE'S, AND A TEST HOLDS IT: one PUT; then the toast; then the caller's
 *  `onSaved` (the page closes the row's form, the preview closes itself); then ONE invalidation of
 *  `templates.set(setKey)` — the prefix of the set's summary, its templates and every class's list, so
 *  the panel, the tree and the decisions page all re-read. Never a plan: that is a sibling key.
 *
 *  ⚠️ A REFUSED SAVE CLOSES NOTHING AND RE-READS NOTHING — the toast states the server's own sentence,
 *  and the form stays open on what the person chose.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */
export const useSaveDecision = (setKey: string, onSaved: () => void) => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const invalidate = (): void => void queryClient.invalidateQueries({ queryKey: queryKeys.templates.set(setKey) });

  return useMutation({
    mutationFn: (body: RecordTemplateDecisionDto) => api.put(`/templates/sets/${setKey}/decisions`, body),
    onSuccess: () => {
      void message.success(DECISIONS_COPY.saved);
      onSaved();
      invalidate();
    },
    onError: (error: ApiError) => void message.error(error.message),
  });
};
